/**
 * MunicipioCombobox — busca de município (por nome/UF) para o onboarding fiscal.
 *
 * - `mode="all"` (MEI): lista TODOS os municípios do Brasil. O MEI emite NFS-e
 *   pelo Padrão Nacional em 100% dos municípios, então não há filtro de adesão.
 * - `mode="adherent"` (ME/EPP): só lista municípios que o app emite — aderentes
 *   ao Emissor Nacional + São Paulo/Fortaleza; município não encontrado = aviso
 *   de que o app ainda não emite para lá.
 *
 * Dataset carregado lazy (~285–360KB) conforme o modo.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  loadMunicipiosNacionais,
  loadTodosMunicipios,
  searchMunicipios,
  type MunicipioNacional,
} from '@/lib/nfseMunicipios';

export function MunicipioCombobox({
  value,
  onChange,
  mode = 'adherent',
}: {
  value: MunicipioNacional | null;
  onChange: (m: MunicipioNacional) => void;
  mode?: 'all' | 'adherent';
}) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<MunicipioNacional[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  // Recarrega quando o modo muda (ex.: usuário troca o tipo de contribuinte).
  useEffect(() => {
    setList([]);
  }, [mode]);

  useEffect(() => {
    if (!open || list.length) return;
    setLoading(true);
    (mode === 'all' ? loadTodosMunicipios() : loadMunicipiosNacionais())
      .then(setList)
      .finally(() => setLoading(false));
  }, [open, list.length, mode]);

  const results = useMemo(() => searchMunicipios(list, query), [list, query]);
  const short = query.trim().length < 2;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {value ? `${value.nome} — ${value.uf}` : 'Selecione o município'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite o nome do município..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && short && (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                Digite ao menos 2 letras para buscar.
              </p>
            )}
            {!loading && !short && results.length === 0 && (
              <p className="px-3 py-4 text-xs text-muted-foreground">
                {mode === 'all' ? (
                  'Nenhum município encontrado. Confira a grafia do nome.'
                ) : (
                  <>
                    Município não disponível para emissão pelo app. Hoje emitimos apenas
                    para municípios aderentes ao <strong>Emissor Nacional</strong> e para
                    São Paulo/Fortaleza.
                  </>
                )}
              </p>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup>
                {results.map((m) => (
                  <CommandItem
                    key={m.ibge}
                    value={m.ibge}
                    onSelect={() => {
                      onChange(m);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4', value?.ibge === m.ibge ? 'opacity-100' : 'opacity-0')}
                    />
                    <span>
                      {m.nome} — {m.uf}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
